/**
 * ManagementTaskMatrix
 *
 * 縦軸: 管理業務タスク (No / 名前 / 業務種別 / 頻度 / 業者 / 法定マーク)
 * 横軸: 1月〜12月 (各セルは「予定」「実績」の 2 領域に分割)
 *
 * 構成: 左ペイン (No / 項目 / 頻度 = 380px 固定) + 右ペイン (12 ヶ月、横スクロール)
 * 横スクロールバーは右ペインの下にだけ表示される。
 * 行高さは厳密 = ROW_HEIGHT (px) で固定し、ペイン間のズレを防ぐ。
 */

import React, { useMemo, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import CustomCheckbox from "../../Checkbox/CustomCheckbox";
import { DragIndicator as DragIndicatorIcon } from "@mui/icons-material";
import {
  DndContext,
  DragOverlay,
  useDndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import CustomLoading from "../../Loading/CustomLoading";
import CustomChip from "../../Chips/CustomChip";
import { colors } from "../../theme/colors";
import type {
  ManagementCellState,
  ManagementTask,
  ManagementTaskResult,
  ManagementTaskType,
} from "../types";

const DEFAULT_MONTHS: number[] = Array.from(
  { length: 12 },
  (_, i) => i + 1,
);

/** 会計年度の開始月から始まる 1〜12月の並びを返す (デフォルトは 1月始まり)。 */
const buildMonthOrder = (startMonth?: number): number[] => {
  if (!startMonth || startMonth < 1 || startMonth > 12) return DEFAULT_MONTHS;
  return Array.from({ length: 12 }, (_, i) => ((startMonth - 1 + i) % 12) + 1);
};

// frequency_unit はモーダル側で daily="週" / quarterly="年" 等とプレースホルダ保存
// される (型に「日」「四半期」が無いため)。表示時は frequency から直接 period を引き、
// frequency_unit には依存しない。
const FREQUENCY_PERIOD_LABEL: Record<
  Exclude<ManagementTask["frequency"], "always">,
  string
> = {
  daily: "日",
  weekly: "週",
  monthly: "月",
  quarterly: "四半期",
  annual: "年",
};

const formatFrequencyDisplay = (task: ManagementTask): string => {
  if (task.frequency === "always") return "常時";
  // frequencyCount が undefined/null のときだけ "-"。
  // 0 は明示的に「0回/期間」として表示する (2026-05: 上限・下限廃止に伴い 0 も許容)。
  if (task.frequencyCount === undefined || task.frequencyCount === null) {
    return "-";
  }
  const period = FREQUENCY_PERIOD_LABEL[task.frequency];
  if (!period) return "-";
  return `${task.frequencyCount}回/${period}`;
};

// 左ペイン列幅 (項目列のみ動的: 最長コンテンツ + 16px、最小 200px)
const COL_W_NO = 90;
const COL_W_NAME_MIN = 200;
const COL_W_NAME_PADDING = 16;
const COL_W_FREQ = 70;
// 「法定」chip + spacing の概算幅 (chip 高さ18 / fontSize 10px、padding と Stack spacing 込み)。
// isLegal=true のタスクがあれば項目列幅にこの分を加算し、chip がはみ出ないようにする。
const LEGAL_CHIP_EXTRA = 44;

// 行高さ (左右ペインで厳密に同じにする — ズレ防止)
const ROW_HEIGHT = 48;
const HEADER_HEIGHT = 48;
/** 予定・実績セル内の円マーク (px) */
const MONTH_MARK_SIZE = 20;

export interface ManagementTaskMatrixProps {
  tasks: ManagementTask[];
  results: ManagementTaskResult[];
  year: number;
  taskTypeFilter?: "all" | ManagementTaskType;
  onEditTask?: (task: ManagementTask) => void;
  onCellClick?: (state: ManagementCellState) => void;
  selectable?: boolean;
  selectedTaskIds?: string[];
  onToggleTaskSelect?: (taskId: string) => void;
  onToggleAll?: (checked: boolean) => void;
  onReorder?: (orderedTaskIds: string[]) => void;
  /** データ取得中はヘッダー下に CustomLoading を表示 (CustomTable と同じ挙動) */
  loading?: boolean;
  /**
   * 会計年度の開始月 (1〜12)。指定すると 12 列の月並びがその月から始まる。
   * 未指定時は 1 月始まり。
   */
  startMonth?: number;
}

const ManagementTaskMatrix: React.FC<ManagementTaskMatrixProps> = ({
  tasks,
  results,
  year: _year,
  taskTypeFilter = "all",
  onEditTask,
  onCellClick,
  selectable = false,
  selectedTaskIds = [],
  onToggleTaskSelect,
  onToggleAll,
  onReorder,
  loading = false,
  startMonth,
}) => {
  const MONTHS = useMemo(() => buildMonthOrder(startMonth), [startMonth]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  // 右ペインの横スクロール検出
  const rightPaneRef = useRef<HTMLDivElement>(null);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const recomputeShadows = React.useCallback(() => {
    const el = rightPaneRef.current;
    if (!el) return;
    setShowRightShadow(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    setShowLeftShadow(el.scrollLeft > 0);
  }, []);
  const handleScroll = () => {
    recomputeShadows();
  };
  React.useEffect(() => {
    recomputeShadows();
    const el = rightPaneRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => recomputeShadows());
    ro.observe(el);
    return () => ro.disconnect();
  }, [tasks.length, recomputeShadows]);

  // ドラッグ中タスク: DragOverlay で行全体 (左+右) を浮かせるために必要
  const [activeId, setActiveId] = useState<string | null>(null);

  const resultMap = useMemo(() => {
    const m = new Map<string, Map<number, ManagementTaskResult>>();
    for (const r of results) {
      let inner = m.get(r.taskId);
      if (!inner) {
        inner = new Map();
        m.set(r.taskId, inner);
      }
      inner.set(r.month, r);
    }
    return m;
  }, [results]);

  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (taskTypeFilter !== "all" && t.taskType !== taskTypeFilter)
        return false;
      return true;
    });
  }, [tasks, taskTypeFilter]);

  // 項目列の幅: 最長の業務名のみを canvas で実測し、+16px のゆとりを取る (最小 200px)。
  // isLegal=true のタスクが 1 件でもあれば、列幅に LEGAL_CHIP_EXTRA を加算して
  // 「法定」chip を含めた幅で項目名を表示できるようにする。長い名前の行は ellipsis で省略。
  // 業者名は計測対象外で、列幅をはみ出す分は CSS の ellipsis で省略する。
  const colWName = useMemo(() => {
    if (typeof document === "undefined") return COL_W_NAME_MIN;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return COL_W_NAME_MIN;
    const bodyFont = getComputedStyle(document.body).fontFamily || "sans-serif";
    let maxWidth = 0;
    ctx.font = `500 13px ${bodyFont}`;
    for (const t of filteredTasks) {
      const w = ctx.measureText(t.name).width;
      if (w > maxWidth) maxWidth = w;
    }
    const hasLegal = filteredTasks.some((t) => t.isLegal);
    const legalExtra = hasLegal ? LEGAL_CHIP_EXTRA : 0;
    return Math.max(
      COL_W_NAME_MIN,
      Math.ceil(maxWidth) + COL_W_NAME_PADDING + legalExtra,
    );
  }, [filteredTasks]);
  const leftPaneWidth = COL_W_NO + colWName + COL_W_FREQ;

  // 業者名は列幅(- 右padding 8px)に収まらなければ JS で文字数を切って末尾に "..." を付ける
  const displayContractor = useMemo(() => {
    const map = new Map<string, string>();
    if (typeof document === "undefined") {
      for (const t of filteredTasks) map.set(t.id, t.contractor || "-");
      return map;
    }
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      for (const t of filteredTasks) map.set(t.id, t.contractor || "-");
      return map;
    }
    const bodyFont = getComputedStyle(document.body).fontFamily || "sans-serif";
    ctx.font = `400 11px ${bodyFont}`;
    const available = colWName - 8;
    const ellipsis = "...";
    const ellipsisW = ctx.measureText(ellipsis).width;
    for (const t of filteredTasks) {
      const c = t.contractor;
      if (!c) {
        map.set(t.id, "-");
        continue;
      }
      if (ctx.measureText(c).width <= available) {
        map.set(t.id, c);
        continue;
      }
      let lo = 0;
      let hi = c.length;
      while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        if (ctx.measureText(c.slice(0, mid)).width + ellipsisW <= available) {
          lo = mid;
        } else {
          hi = mid - 1;
        }
      }
      map.set(t.id, lo > 0 ? c.slice(0, lo) + ellipsis : ellipsis);
    }
    return map;
  }, [filteredTasks, colWName]);

  const allSelected =
    filteredTasks.length > 0 &&
    filteredTasks.every((t) => selectedTaskIds.includes(t.id));

  // ──────────────────────────────────────────
  // 1 セル (予定 + 実績の 2 領域)
  // ──────────────────────────────────────────
  const renderMonthCell = (
    task: ManagementTask,
    month: number,
  ): React.ReactNode => {
    const result = resultMap.get(task.id)?.get(month);
    const hasPlanned = result?.planned || false;
    const hasCompleted = result?.completed || false;
    const plannedDates = result?.plannedDates || [];
    const executionRecords = result?.executionRecords || [];
    const firstPlannedDay = plannedDates[0];
    const lastCompletedDay = (() => {
      if (executionRecords.length === 0) return undefined;
      const days = executionRecords
        .map((r) => Number(r.date.slice(8, 10)))
        .filter((d) => Number.isFinite(d));
      if (days.length === 0) return undefined;
      return Math.max(...days);
    })();
    // 予定 / 実績 はそれぞれ独立に「法定 / リモート」を持つ (択一)。
    // R アイコン表示は「リモート」が選択されているときのみ (法定 or 未指定 は通常円)。
    const plannedIsRemote = !!result?.plannedIsRemote;
    const actualIsRemote = !!result?.actualIsRemote;

    const completedBg = colors.primary.main;
    const plannedBorder = colors.primary.main;

    const handleClick = () => {
      onCellClick?.({
        taskId: task.id,
        month,
        planned: hasPlanned,
        plannedDates,
        completed: hasCompleted,
        executionRecords,
      });
    };

    return (
      <Box
        onClick={handleClick}
        data-testid="management-task-cell"
        data-task-id={task.id}
        data-month={month}
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          height: "100%",
          cursor: onCellClick ? "pointer" : "default",
          // 月の縦区切り線は呼び出し側のラッパー Box (Level 1) で描画する。
          // ここに borderLeft を持たせると、ドラッグ中の visibility 切替で消えるため。
          "&:hover": onCellClick
            ? { backgroundColor: colors.background.selected }
            : undefined,
        }}
      >
        {/* 予定 */}
        <Box
          sx={{
            borderRight: `1px solid ${colors.border.light}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 0.5,
          }}
        >
          {hasPlanned ? (
            <>
              {plannedIsRemote ? (
                <Box
                  sx={{
                    width: MONTH_MARK_SIZE,
                    height: MONTH_MARK_SIZE,
                    boxSizing: "border-box",
                    borderRadius: "50%",
                    border: `2px solid ${plannedBorder}`,
                    color: plannedBorder,
                    fontSize: "11px",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  R
                </Box>
              ) : (
                <Box
                  sx={{
                    width: MONTH_MARK_SIZE,
                    height: MONTH_MARK_SIZE,
                    boxSizing: "border-box",
                    borderRadius: "50%",
                    border: `2px solid ${plannedBorder}`,
                  }}
                />
              )}
              <Typography
                sx={{ fontSize: "10px", color: colors.text.secondary }}
              >
                {plannedDates.length > 1
                  ? `${plannedDates.length}回`
                  : firstPlannedDay
                    ? `${firstPlannedDay}日`
                    : "-日"}
              </Typography>
            </>
          ) : null}
        </Box>
        {/* 実績 */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 0.5,
          }}
        >
          {hasCompleted ? (
            <>
              {actualIsRemote ? (
                <Box
                  sx={{
                    width: MONTH_MARK_SIZE,
                    height: MONTH_MARK_SIZE,
                    borderRadius: "50%",
                    backgroundColor: completedBg,
                    color: colors.text.inverse,
                    fontSize: "11px",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  R
                </Box>
              ) : (
                <Box
                  sx={{
                    width: MONTH_MARK_SIZE,
                    height: MONTH_MARK_SIZE,
                    borderRadius: "50%",
                    backgroundColor: completedBg,
                  }}
                />
              )}
              <Typography
                sx={{ fontSize: "10px", color: colors.text.secondary }}
              >
                {plannedDates.length > 1
                  ? `${executionRecords.length}回`
                  : lastCompletedDay
                    ? `${lastCompletedDay}日`
                    : "-日"}
              </Typography>
            </>
          ) : null}
        </Box>
      </Box>
    );
  };

  // ──────────────────────────────────────────
  // 左ペインの 1 行 — sortable 本体 (drag handle / 選択 / No / 項目 / 頻度)
  // ──────────────────────────────────────────
  const LeftRow: React.FC<{ task: ManagementTask }> = ({ task }) => {
    const isSelected = selectedTaskIds.includes(task.id);
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: task.id });
    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      // ドラッグ中は中身だけ非表示にして行枠 (border) は残す。
      // opacity:0 にすると borderTop も透明になり、上下の行の境界線が消えて見える。
    };
    return (
      <Box
        ref={setNodeRef}
        style={style}
        data-testid="management-task-row"
        data-task-id={task.id}
        sx={{
          display: "grid",
          gridTemplateColumns: `${COL_W_NO}px ${colWName}px ${COL_W_FREQ}px`,
          alignItems: "stretch",
          height: ROW_HEIGHT,
          boxSizing: "border-box",
          borderTop: `1px solid ${colors.border.light}`,
          backgroundColor: colors.background.default,
          overflow: "hidden",
          // ドラッグ中は孫要素以下を不可視にして、行の borderTop と
          // 列ラッパー Box (頻度列の borderLeft 等) は保持する
          ...(isDragging && {
            "& > * > *": { visibility: "hidden" },
          }),
          "& .drag-handle": {
            opacity: 0,
            transition: "opacity 0.15s ease",
          },
          "&:hover .drag-handle": { opacity: 1 },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ px: 1, height: "100%" }}
        >
          <Box
            {...attributes}
            {...listeners}
            className="drag-handle"
            data-testid="management-task-drag-handle"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 22,
              height: 22,
              borderRadius: "4px",
              cursor: "grab",
              touchAction: "none",
              transition: "background-color 0.15s ease",
              "&:hover": { backgroundColor: colors.background.hover },
              "&:active": {
                cursor: "grabbing",
                backgroundColor: colors.background.selected,
              },
            }}
          >
            <DragIndicatorIcon
              sx={{ fontSize: 16, color: colors.text.disabled }}
            />
          </Box>
          {selectable ? (
            <CustomCheckbox
              size="extra-small"
              checked={isSelected}
              onChange={() => onToggleTaskSelect?.(task.id)}
            />
          ) : null}
          <Typography
            sx={{ ml: "auto", mr: 1, fontSize: "13px", fontWeight: 500 }}
          >
            {task.no}.
          </Typography>
        </Stack>
        <Stack
          direction="column"
          spacing={0.25}
          justifyContent="center"
          sx={{ height: "100%", pl: 0, pr: 1, minWidth: 0 }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ minWidth: 0 }}
          >
            <Typography
              data-testid="management-task-name"
              onClick={() => onEditTask?.(task)}
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: colors.text.primary,
                cursor: onEditTask ? "pointer" : "default",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
                flexShrink: 1,
                "&:hover": onEditTask
                  ? {
                      color: colors.primary.main,
                      textDecoration: "underline",
                    }
                  : undefined,
              }}
            >
              {task.name}
            </Typography>
            {task.isLegal && (
              <Box sx={{ flexShrink: 0 }}>
                <CustomChip
                  label="法定"
                  backgroundColor={colors.primary.light}
                  color={colors.primary.dark}
                  fontSize="10px"
                  height={18}
                />
              </Box>
            )}
          </Stack>
          <Typography
            sx={{
              fontSize: "11px",
              color: colors.text.secondary,
              whiteSpace: "nowrap",
            }}
          >
            {displayContractor.get(task.id) || "-"}
          </Typography>
        </Stack>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderLeft: `1px solid ${colors.border.light}`,
            height: "100%",
          }}
        >
          <Typography sx={{ fontSize: "12px", whiteSpace: "nowrap" }}>
            {formatFrequencyDisplay(task)}
          </Typography>
        </Box>
      </Box>
    );
  };

  // ──────────────────────────────────────────
  // 右ペインの 1 行 (12 ヶ月)
  // useDndContext から active/over を取得し、左ペイン (useSortable) のシフトを手動で再現する。
  // vertical sortable の挙動:
  //   - active を下にドラッグ (activeIndex < overIndex): (activeIndex, overIndex] の行は -ROW_HEIGHT
  //   - active を上にドラッグ (activeIndex > overIndex): [overIndex, activeIndex) の行は +ROW_HEIGHT
  // ──────────────────────────────────────────
  const RightRow: React.FC<{ task: ManagementTask; index: number }> = ({
    task,
    index,
  }) => {
    const { active, over } = useDndContext();
    const isDragging = active?.id === task.id;

    let translateY = 0;
    if (active) {
      const activeIndex = filteredTasks.findIndex((t) => t.id === active.id);
      const overIndex = over
        ? filteredTasks.findIndex((t) => t.id === over.id)
        : activeIndex;
      if (activeIndex !== -1 && overIndex !== -1) {
        if (isDragging) {
          // ドラッグ中の本人は overIndex の位置にスライドさせ、
          // 「シフト後の隣接行」とのあいだに空きスロットが出来ないようにする
          // (= 非ドラッグ行の borderTop が連続して見える)
          translateY = (overIndex - activeIndex) * ROW_HEIGHT;
        } else if (
          activeIndex < overIndex &&
          index > activeIndex &&
          index <= overIndex
        ) {
          translateY = -ROW_HEIGHT;
        } else if (
          activeIndex > overIndex &&
          index >= overIndex &&
          index < activeIndex
        ) {
          translateY = ROW_HEIGHT;
        }
      }
    }

    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(12, minmax(80px, 1fr))",
          alignItems: "stretch",
          height: ROW_HEIGHT,
          boxSizing: "border-box",
          borderTop: `1px solid ${colors.border.light}`,
          backgroundColor: colors.background.default,
          // ドラッグ中は最深部 (予定/実績 ボックス内の icon / Typography) のみ非表示。
          //   - 行 (level 0) の borderTop ✓ 保持
          //   - 月ラッパー (level 1) の borderLeft ✓ 保持
          //   - 予定/実績 ボックス (level 3) の borderRight ✓ 保持
          ...(isDragging && {
            "& > * > * > * > *": { visibility: "hidden" },
          }),
          transform: `translate3d(0, ${translateY}px, 0)`,
          transition: "transform 200ms ease",
        }}
      >
        {MONTHS.map((m) => (
          <Box
            key={m}
            sx={{
              height: "100%",
              borderLeft: `1px solid ${colors.border.light}`,
            }}
          >
            {renderMonthCell(task, m)}
          </Box>
        ))}
      </Box>
    );
  };

  // DragOverlay 用: 行全体 (左+右) を一体で浮かせる視覚クローン
  const OverlayRow: React.FC<{ task: ManagementTask }> = ({ task }) => (
    <Box
      sx={{
        display: "flex",
        height: ROW_HEIGHT,
        boxSizing: "border-box",
        backgroundColor: colors.background.selected,
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        borderRadius: "4px",
        overflow: "hidden",
        opacity: 0.95,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `${COL_W_NO}px ${colWName}px ${COL_W_FREQ}px`,
          width: leftPaneWidth,
          flexShrink: 0,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ px: 1, height: "100%" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 22,
              height: 22,
            }}
          >
            <DragIndicatorIcon
              sx={{ fontSize: 16, color: colors.text.disabled }}
            />
          </Box>
          {selectable && <Box sx={{ width: 18 }} />}
          <Typography
            sx={{ ml: "auto", mr: 1, fontSize: "13px", fontWeight: 500 }}
          >
            {task.no}.
          </Typography>
        </Stack>
        <Stack
          direction="column"
          spacing={0.25}
          justifyContent="center"
          sx={{ height: "100%", pl: 0, pr: 1, minWidth: 0 }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ minWidth: 0 }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: colors.text.primary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
                flexShrink: 1,
              }}
            >
              {task.name}
            </Typography>
            {task.isLegal && (
              <Box sx={{ flexShrink: 0 }}>
                <CustomChip
                  label="法定"
                  backgroundColor={colors.primary.light}
                  color={colors.primary.dark}
                  fontSize="10px"
                  height={18}
                />
              </Box>
            )}
          </Stack>
          <Typography
            sx={{
              fontSize: "11px",
              color: colors.text.secondary,
              whiteSpace: "nowrap",
            }}
          >
            {displayContractor.get(task.id) || "-"}
          </Typography>
        </Stack>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderLeft: `1px solid ${colors.border.light}`,
            height: "100%",
          }}
        >
          <Typography sx={{ fontSize: "12px", whiteSpace: "nowrap" }}>
            {formatFrequencyDisplay(task)}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(12, 80px)",
          flexShrink: 0,
        }}
      >
        {MONTHS.map((m) => (
          <Box
            key={m}
            sx={{
              height: "100%",
              borderLeft: `1px solid ${colors.border.light}`,
            }}
          >
            {renderMonthCell(task, m)}
          </Box>
        ))}
      </Box>
    </Box>
  );

  // ──────────────────────────────────────────
  // ヘッダ (左 / 右)
  // ──────────────────────────────────────────
  const headerBg = colors.background.lighter;

  const LeftHeader: React.FC = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `${COL_W_NO}px ${colWName}px ${COL_W_FREQ}px`,
        backgroundColor: headerBg,
        borderBottom: `1px solid ${colors.border.light}`,
        height: HEADER_HEIGHT,
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pl: 4.7,
          pr: 1,
          height: "100%",
        }}
      >
        {selectable && (
          <CustomCheckbox
            size="extra-small"
            checked={allSelected}
            onChange={() => onToggleAll?.(!allSelected)}
          />
        )}
        <Typography
          sx={{
            ml: "auto",
            mr: 1,
            fontSize: "12px",
            fontWeight: 600,
            color: colors.text.primary,
          }}
        >
          No.
        </Typography>
      </Box>
      <Box
        sx={{
          pl: 0.2,
          pr: 1,
          display: "flex",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            color: colors.text.primary,
          }}
        >
          項目
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderLeft: `1px solid ${colors.border.light}`,
          height: "100%",
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            color: colors.text.primary,
          }}
        >
          頻度
        </Typography>
      </Box>
    </Box>
  );

  const RightHeader: React.FC = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(12, minmax(80px, 1fr))",
        backgroundColor: headerBg,
        borderBottom: `1px solid ${colors.border.light}`,
        height: HEADER_HEIGHT,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {MONTHS.map((m) => (
        <Box
          key={m}
          sx={{
            borderLeft: `1px solid ${colors.border.light}`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            py: 0.75,
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              textAlign: "center",
              color: colors.text.primary,
              borderBottom: `1px solid ${colors.border.light}`,
              pb: 0.25,
            }}
          >
            {m}月
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              pt: 0.25,
            }}
          >
            <Typography
              sx={{
                fontSize: "10px",
                color: colors.text.secondary,
                textAlign: "center",
              }}
            >
              予定
            </Typography>
            <Typography
              sx={{
                fontSize: "10px",
                color: colors.text.secondary,
                textAlign: "center",
              }}
            >
              実績
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };
  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = filteredTasks.map((t) => t.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder?.(arrayMove(ids, oldIndex, newIndex));
  };
  const handleDragCancel = () => setActiveId(null);

  const activeTask = useMemo(
    () => (activeId ? filteredTasks.find((t) => t.id === activeId) : null),
    [activeId, filteredTasks],
  );

  return (
    <Box
      data-testid="management-task-matrix"
      sx={{
        border: `1px solid ${colors.border.light}`,
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: colors.background.default,
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: 12,
          pointerEvents: "none",
          background:
            "linear-gradient(to left, rgba(0,0,0,0.12), rgba(0,0,0,0))",
          zIndex: 5,
          opacity: showRightShadow ? 1 : 0,
          transition: "opacity 0.4s ease",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          bottom: 0,
          left: leftPaneWidth,
          width: 12,
          pointerEvents: "none",
          background:
            "linear-gradient(to right, rgba(0,0,0,0.12), rgba(0,0,0,0))",
          zIndex: 5,
          opacity: showLeftShadow ? 1 : 0,
          transition: "opacity 0.4s ease",
        },
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        autoScroll={false}
      >
        <SortableContext
          items={filteredTasks.map((t) => t.id)}
          strategy={verticalListSortingStrategy}
        >
          <Box sx={{ display: "flex" }}>
            {/* 左ペイン (固定、スクロール無し) */}
            <Box sx={{ width: leftPaneWidth, flexShrink: 0 }}>
              <LeftHeader />
              {!loading &&
                filteredTasks.map((t) => <LeftRow key={t.id} task={t} />)}
              {!loading && filteredTasks.length === 0 && (
                <Box sx={{ height: ROW_HEIGHT * 3 }} />
              )}
              {loading && <Box sx={{ height: ROW_HEIGHT * 3 }} />}
            </Box>
            {/* 右ペイン (横スクロール) */}
            <Box
              ref={rightPaneRef}
              onScroll={handleScroll}
              sx={{
                flex: 1,
                minWidth: 0,
                overflowX: "auto",
                overflowY: "hidden",
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": { height: 8 },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: colors.border.medium,
                  borderRadius: 4,
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: colors.text.disabled,
                },
              }}
            >
              <Box
                sx={{
                  minWidth: 12 * 80,
                  position: "relative",
                  // 内部に下線を持たせて、最終行の下端を明示する。
                  // marginBottom: -1px で outer wrapper の borderBottom と
                  // 重ねて 1px 線になるよう調整 (2 重防止)
                  borderBottom: `1px solid ${colors.border.light}`,
                  marginBottom: "-1px",
                }}
              >
                <RightHeader />
                {!loading &&
                  filteredTasks.map((t, idx) => (
                    <RightRow key={t.id} task={t} index={idx} />
                  ))}
                {!loading && filteredTasks.length === 0 && (
                  <Box
                    sx={{
                      height: ROW_HEIGHT * 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: colors.text.secondary,
                    }}
                  >
                    <Typography sx={{ fontSize: "13px" }}>
                      管理業務が登録されていません
                    </Typography>
                  </Box>
                )}
                {loading && (
                  <Box
                    sx={{
                      position: "absolute",
                      // ヘッダー (月 + 予定/実績) の下に表示
                      top: `${HEADER_HEIGHT}px`,
                      left: 0,
                      right: 0,
                      height: ROW_HEIGHT * 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      // 左ペイン側の余白も覆う
                      ml: `-${leftPaneWidth}px`,
                      width: `calc(100% + ${leftPaneWidth}px)`,
                      pointerEvents: "none",
                    }}
                  >
                    <CustomLoading
                      loading={true}
                      fullScreen={false}
                      size="medium"
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </SortableContext>
        {/* ドラッグ中の行全体 (左 + 右) を浮かせる。
            DragOverlay の既定幅は dragged element (LeftRow=leftPaneWidth) に
            合わさるので、style で 12 ヶ月分まで明示的に拡張する。 */}
        <DragOverlay
          dropAnimation={null}
          style={{
            width: leftPaneWidth + 12 * 80,
            height: ROW_HEIGHT,
          }}
        >
          {activeTask ? <OverlayRow task={activeTask} /> : null}
        </DragOverlay>
      </DndContext>
    </Box>
  );
};

export default ManagementTaskMatrix;
